



















































































































































import { Component, Vue, Watch } from "vue-property-decorator";
import { BOverlay } from "bootstrap-vue";

import CustomSelect from "@/layouts/components/Select.vue";
import CustomButton from "@/layouts/components/Button.vue";
import BaseModal from "@/layouts/components/BaseModal.vue";
import EntityCrud from "../entity/EntityCrud.vue";

import SolutionModel from "@/api/solution.model";
import SolutionByPlanAdapter from "@/api/solution_by_plan_adapter";
import CorporateClientModel from "@/api/corporate_client_app.model";
import InsurerPlanModel from "@/api/insurer_plan_app.model";
import SolutionCategoryModel from "@/api/solution_category.model";
import { showErrorAlert, showSuccessAlert } from "@/helpers";
import { SolutionStatus, translateSolutionStatus } from "@/interfaces/solution";

@Component({
	components: {
		CustomSelect,
		CustomButton,
		BaseModal,
		BOverlay,
		EntityCrud,
	},
})
export default class SolutionAssociation extends Vue {
	adapter: any = SolutionByPlanAdapter;

	// Filtros principais
	selectedCorporateClient: any = null;
	selectedPlans: any[] = [];

	// Seleção
	selectedSolutions: any[] = [];
	selectedSolutionIds: string[] = [];

	// Estado
	isProcessing = false;
	isLoadingInitial = true;

	// Options para selects
	corporateClientOptions: any[] = [];
	planOptions: any[] = [];
	categoryOptions: any[] = [];

	// Modals
	showAssociateModal = false;
	showConfirmModal = false;

	// Computed
	get filteredPlanOptions() {
		if (!this.selectedCorporateClient) return [];
		return this.planOptions.filter(
			(option: any) => option.value?.corporateClientId === this.selectedCorporateClient.id,
		);
	}

	get tableKey() {
		const clientId = this.selectedCorporateClient?.id || "no-client";
		const planIds = (this.selectedPlans || []).map((p: any) => p.id).join("-");
		return `${clientId}-${planIds}`;
	}

	get associationFilter() {
		if (!this.selectedPlans || this.selectedPlans.length === 0) return {};
		return {
			planIds: this.selectedPlans.map((plan: any) => plan.id),
			...(this.selectedCorporateClient ? { corporateClientId: this.selectedCorporateClient.id } : {}),
		};
	}

	get tableColumns() {
		return [
			{
				key: "status",
				name: "Status",
				kind: "customTag",
				styleTags: {
					CONFIRMED: "primary",
					REFUSED: "secondary",
					VALIDATE: "tertiary",
					INACTIVATED: "quaternary",
				},
				hasSelect: true,
				options: [
					{ name: "Validar", value: "VALIDATE" },
					{ name: "Confirmado", value: "CONFIRMED" },
					{ name: "Recusado", value: "REFUSED" },
					{ name: "Inativada", value: "INACTIVATED" },
				],
				translated: translateSolutionStatus,
				width: "120px",
				isReadOnly: true,
			},
			{
				key: "isFavorite",
				filterKey: "isFavorite",
				kind: "icon",
				icon: "star.svg",
				subMenuStyle: "items-center",
				width: "80px",
				isReadOnly: true,
				hasSelect: true,
				options: [
					{ icon: "star.svg", value: true },
					{ icon: "star.svg", value: false, iconStyle: "filter grayscale" },
				],
			},
			{
				key: "name",
				name: "Nome da solução",
				style: "bold",
				isReadOnly: true,
			},
			{
				key: "categoryName",
				name: "Categoria",
				filterKey: "categoryId",
				hasSelect: true,
				options: this.categoryOptions.map(c => ({ name: c.name, value: c.value })),
				isReadOnly: true,
			},
			{
				key: "isAssociated",
				name: "Associação",
				kind: "actionTag",
				filterKey: "isAssociated",
				styleTags: {
					Associado: "primary",
					"Não associado": "quaternary",
				},
				hasSelect: true,
				options: [
					{ name: "Associado", value: true },
					{ name: "Não associado", value: false },
				],
				isReadOnly: true,
			},
		];
	}

	// Lifecycle
	async mounted() {
		await this.loadInitialData();
	}

	// Watchers
	@Watch("selectedCorporateClient")
	onCorporateClientChange() {
		this.selectedPlans = [];
		this.clearSelection();
	}

	// Methods
	async loadInitialData() {
		try {
			const corporateClientsResponse = await CorporateClientModel.search({});
			const corporateClientsData = Array.isArray(corporateClientsResponse)
				? corporateClientsResponse
				: corporateClientsResponse.data || [];
			this.corporateClientOptions = corporateClientsData.map((client: any) => ({
				name: client.name,
				value: { id: client.id, name: client.name },
			}));

			const plansResponse = await InsurerPlanModel.search({ page: 1, limit: 0 });
			const plansData = Array.isArray(plansResponse) ? plansResponse : plansResponse.data || [];
			this.planOptions = plansData.map((plan: any) => ({
				name: plan.name,
				value: {
					id: plan.id,
					name: plan.name,
					corporateClientId: plan.corporateClient?.id,
				},
			}));

			const categoriesResponse = await SolutionCategoryModel.search({});
			const categoriesData = Array.isArray(categoriesResponse) ? categoriesResponse : categoriesResponse.data || [];
			this.categoryOptions = categoriesData.map((category: any) => ({
				name: category.name,
				value: category.id,
			}));
		} catch (error) {
			showErrorAlert("Erro ao carregar dados. Tente novamente.");
		} finally {
			this.isLoadingInitial = false;
		}
	}

	// Seleção
	onUpdateSelected(selected: any[]) {
		this.selectedSolutions = selected;
		this.selectedSolutionIds = selected.map(s => s.id);
	}

	clearSelection() {
		this.selectedSolutions = [];
		this.selectedSolutionIds = [];
	}

	// Toggle associação individual
	async onToggleAssociation(payload: { key: string; data: any }) {
		if (!this.selectedPlans || this.selectedPlans.length === 0 || this.isProcessing) return;

		const data = payload.data;
		if (!data?.id) return;

		this.isProcessing = true;
		try {
			if (data.isAssociated === "Associado") {
				// Desassociar de todos os planos selecionados
				await Promise.all(
					this.selectedPlans.map((plan: any) =>
						SolutionModel.bulkUnassignPlan({
							solutionIds: [data.id],
							insurerPlanId: plan.id,
						}),
					),
				);
				showSuccessAlert("Solução desassociada com sucesso.");
			} else {
				// Associar a todos os planos selecionados
				const planIds = this.selectedPlans.map((plan: any) => plan.id);
				await SolutionModel.bulkAssignPlans({
					solutionIds: [data.id],
					insurerPlanIds: planIds,
				});
				showSuccessAlert("Solução associada com sucesso.");
			}
			this.refreshTable();
		} catch (error: any) {
			showErrorAlert(error?.response?.message || "Erro ao alternar associação.");
		} finally {
			this.isProcessing = false;
		}
	}

	// Ações bulk
	async associateSelected() {
		if (!this.selectedPlans || this.selectedPlans.length === 0 || this.selectedSolutionIds.length === 0) return;

		const notAssociatedIds = this.selectedSolutions.filter(s => s.isAssociated === "Não associado").map(s => s.id);

		if (notAssociatedIds.length === 0) {
			showSuccessAlert("Todas as soluções selecionadas já estão associadas.");
			this.closeAssociateModal();
			return;
		}

		this.isProcessing = true;
		try {
			const planIds = this.selectedPlans.map((plan: any) => plan.id);
			const response = await SolutionModel.bulkAssignPlans({
				solutionIds: notAssociatedIds,
				insurerPlanIds: planIds,
			});

			showSuccessAlert(response.message);
			this.clearSelection();
			this.closeAssociateModal();
			this.refreshTable();
		} catch (error: any) {
			showErrorAlert(error?.response?.message || "Erro ao associar soluções.");
		} finally {
			this.isProcessing = false;
		}
	}

	async unassociateSelected() {
		if (!this.selectedPlans || this.selectedPlans.length === 0 || this.selectedSolutionIds.length === 0) return;

		const associatedIds = this.selectedSolutions.filter(s => s.isAssociated === "Associado").map(s => s.id);

		if (associatedIds.length === 0) {
			showSuccessAlert("Nenhuma das soluções selecionadas está associada.");
			this.closeConfirmModal();
			return;
		}

		this.isProcessing = true;
		try {
			// Desassociar cada solução de cada plano selecionado
			await Promise.all(
				this.selectedPlans.map((plan: any) =>
					SolutionModel.bulkUnassignPlan({
						solutionIds: associatedIds,
						insurerPlanId: plan.id,
					}),
				),
			);

			showSuccessAlert("Soluções desassociadas com sucesso.");
			this.clearSelection();
			this.closeConfirmModal();
			this.refreshTable();
		} catch (error: any) {
			showErrorAlert(error?.response?.message || "Erro ao desassociar soluções.");
		} finally {
			this.isProcessing = false;
		}
	}

	refreshTable() {
		(this.$refs.entityCrud as any)?.refresh();
	}

	// Modals
	showAssociateConfirmation() {
		this.showAssociateModal = true;
	}

	closeAssociateModal() {
		this.showAssociateModal = false;
	}

	showUnassociateConfirmation() {
		this.showConfirmModal = true;
	}

	closeConfirmModal() {
		this.showConfirmModal = false;
	}

	goBack() {
		this.$router.push("/solucoes");
	}
}
